import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { agentProductionTerms } from '../../constants';
import { getTooltipContent } from '../helpers/helpers';
import { getBarHeight, getDataLabel } from '../helpers/chart';
import { marketShareTerms } from '../constants';
import Loader from '@lwt-helix/loader';
import { useSidebarExpanded } from '../../common/hooks/useSidebarExpanded';

const SIDEBAR_EXPANDED_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 68;
const SCROLL_BREAKPOINT = 1200;
const MINIMUM_CHART_WIDTH = 1200;

export const ResultsChart = props => {
    const { data, config: { rank, chart: chartConfig }, hasSelectedRows, isRenderingToFile } = props;
    const { isSideNavExpanded, isTransitioning } = useSidebarExpanded();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const wasSidebarTransitioning = useRef(false);

    const sidebarWidth = isSideNavExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
    const availableWidth = windowWidth - sidebarWidth + 200;
    const needsScroll = !isRenderingToFile && availableWidth < SCROLL_BREAKPOINT;

    const yAxisLabels = data?.map((item) => chartConfig.yAxisLabelMapper(item) ?? '') ?? [];

    const getDataPointGroups = (groups) => {
        return groups?.map(group => {
            const groupData = data?.map(group.dataMapper);
            return {
                name: group.label,
                data: groupData
            };
        });
    };

    const barColors = chartConfig?.dataPointGroups?.map(dataGroup => dataGroup.color);

    const dataPointGroups = getDataPointGroups(chartConfig?.dataPointGroups);

    const fontFamily = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,' +
        'sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"';

    const options = {
        rank,
        grid: {
            yaxis: { lines: { show: false } },
            xaxis: { lines: { show: true } },
            strokeDashArray: 3
        },
        chart: {
            background: '#fff',
            animations: {
                enabled: false
            },
            type: 'bar',
            stacked: true,
            toolbar: {
                show: false
            },
            fontFamily,
            noData: {
                text: agentProductionTerms.noDataForSelectedAgent
            },
            zoom: {
                enabled: false
            }
        },
        tooltip: {
            custom: ({ series, dataPointIndex, w: graphInfo }) => {
                return getTooltipContent(series, dataPointIndex, graphInfo, barColors, chartConfig);
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                legend: {
                    position: 'left',
                    offsetX: -10,
                    offsetY: 0
                }
            }
        }],
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 1,
                barHeight: getBarHeight(yAxisLabels?.length),
                dataLabels: {
                    position: 'top',
                    hideOverflowingLabels: false
                }
            },
        },
        dataLabels: {
            enabled: true,
            offsetX: isRenderingToFile ? 100 : 70,
            offsetY: isRenderingToFile ? -3 : 0,
            formatter: function (_, { seriesIndex, dataPointIndex, w }) {
                return getDataLabel(seriesIndex, dataPointIndex, w, data);
            },
            style: {
                colors: ['#000000'],
                fontSize: isRenderingToFile ? '20px' : '12px'
            }
        },
        xaxis: {
            categories: yAxisLabels,
            labels: {
                offsetY: isRenderingToFile ? 3 : 0,
                formatter: chartConfig?.xAxisLabelFormatter,
                style: {
                    fontWeight: 400,
                    fontSize: isRenderingToFile ? '20px' : '16px',
                    colors: isRenderingToFile ? '#000' : '#9CA3AF'
                }
            }
        },
        yaxis: {
            title: {
                text: undefined
            },
            labels: {
                maxWidth: screen.width * 0.40,
                style: {
                    fontWeight: 400,
                    fontSize: isRenderingToFile ? '20px' : '16px',
                    colors: isRenderingToFile ? '#000' : '#4B5563'
                },
                offsetX: 10
            }
        },
        legend: {
            inverseOrder: false,
            markers: {
                fillColors: barColors,
                width: 15,
                height: 15,
                radius: 10
            },
            fontFamily,
            itemMargin: {
                horizontal: 10
            },
            onItemClick: {
                toggleDataSeries: false
            },
            position: 'bottom',
            horizontalAlign: 'left',
            showForSingleSeries: true,
            fontSize: '16px',
            labels: {
                colors: '#4B5563',
            }
        },
        fill: {
            opacity: 1,
            colors: barColors
        }
    };

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize, true);
        return () => window.removeEventListener('resize', handleResize, true);
    }, []);

    useEffect(() => {
        if (wasSidebarTransitioning.current && !isTransitioning && !isRenderingToFile) {
            window.dispatchEvent(new Event('resize'));
        }
        wasSidebarTransitioning.current = isTransitioning;
    }, [isTransitioning, isRenderingToFile]);

    const isShowEmptyState = data === undefined;
    const chartProps = {
        options,
        series: dataPointGroups,
        type: 'bar',
        height: 372,
        width: needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%'
    };
    
    return <div className='market-share-chart-container'>
        {isShowEmptyState
            ? null
            : !hasSelectedRows
                ? <div className='content text-center'>
                    <p className='initial-search mb-5 mt-5'>{marketShareTerms.noRowsSelected}</p>
                </div>
                : <div id='market-share-chart-graph' className={`raised-border mb-3 bg-white market-share-chart ${isRenderingToFile ? 'chart-capture-area-0' : ''} ${needsScroll ? 'needs-scroll' : 'no-scroll'}`}>
                    {!isRenderingToFile && isTransitioning ? (
                        <div className='chart-loader'>
                            <Loader />
                        </div>
                    ) : (
                        <div className={`chart-wrapper ${needsScroll ? 'needs-scroll' : ''}`}>
                            <Chart {...chartProps} />
                        </div>
                    )}
                </div>
        }
    </div>;
};

ResultsChart.propTypes = {
    data: PropTypes.array,
    config: PropTypes.object,
    hasSelectedRows: PropTypes.number.isRequired,
    rank: PropTypes.string,
    isRenderingToFile: PropTypes.bool,
};
